import { useEffect } from "react";
import { fetchDashboardStatistics } from "../services/dashboardStatisticsService";
import {
  ACTIVE_BROKER_STATS_POLL_MS,
  INACTIVE_BROKER_STATS_POLL_MS,
  normalizeBrokerFlag,
  useBrokerStatisticsStore,
} from "../stores/brokerStatisticsStore";

export function useBrokerStatisticsPolling(pmsApiBaseUrl, activeBrokerFlag) {
  const brokerFlagsKey = useBrokerStatisticsStore((state) =>
    Object.keys(state.statsByBroker).sort().join("|"),
  );

  useEffect(() => {
    const email = localStorage.getItem("email") || "";
    const normalizedActiveFlag = normalizeBrokerFlag(activeBrokerFlag);

    if (!email || !normalizedActiveFlag) return;

    const store = useBrokerStatisticsStore.getState();
    if (!store.isInitialized) {
      store.initializeFromUserBrokers();
    }

    let cancelled = false;
    let activeInFlight = false;
    let inactiveInFlight = false;

    const pollActiveBroker = async () => {
      if (cancelled || activeInFlight) return;
      activeInFlight = true;

      try {
        const result = await fetchDashboardStatistics({
          pmsApiBaseUrl,
          email,
          brokerFlag: normalizedActiveFlag,
          syncMargin: true,
        });

        if (cancelled || !result.ok || !result.stats) return;

        useBrokerStatisticsStore
          .getState()
          .setBrokerStats(normalizedActiveFlag, result.stats, { applyZeroGuard: true });
      } finally {
        activeInFlight = false;
      }
    };

    const pollInactiveBrokers = async () => {
      if (cancelled || inactiveInFlight) return;
      inactiveInFlight = true;

      try {
        const inactiveFlags = useBrokerStatisticsStore
          .getState()
          .getInactiveBrokerFlags();

        await Promise.all(
          inactiveFlags.map(async (brokerFlag) => {
            const result = await fetchDashboardStatistics({
              pmsApiBaseUrl,
              email,
              brokerFlag,
              syncMargin: false,
            });

            if (cancelled || !result.ok || !result.stats) return;

            useBrokerStatisticsStore
              .getState()
              .setBrokerStats(brokerFlag, result.stats, { applyZeroGuard: false });
          }),
        );
      } finally {
        inactiveInFlight = false;
      }
    };

    void pollActiveBroker();

    const activeIntervalId = setInterval(pollActiveBroker, ACTIVE_BROKER_STATS_POLL_MS);

    const inactiveIntervalId = setInterval(
      pollInactiveBrokers,
      INACTIVE_BROKER_STATS_POLL_MS,
    );

    return () => {
      cancelled = true;
      clearInterval(activeIntervalId);
      clearInterval(inactiveIntervalId);
    };
  }, [pmsApiBaseUrl, activeBrokerFlag, brokerFlagsKey]);
}
